/**
 * The workspace switch's sheet in the new design (PV5-8:D part 2, 2026-10-13), written from ./switchLooks.ts.
 *
 * The overlay rides the first download (it must cover the page the moment a switch begins, and the moment the app
 * mounts after the reload), so it carries this sheet itself, in a <style> inside its own root: nothing of the kit's or
 * the shell's sheets, which load on demand, is needed. The sky is opaque in both lights: no page shows through it.
 *
 * Its rungs: the overlay stands on BODY_PORTAL_Z_INDEX, as today's does, above every chat layer and every dialog.
 */
import { BODY_PORTAL_Z_INDEX } from '../../../styles/layers';
import { SWITCH_FADE_MS } from '../../../hooks/workspaceSwitch/useWorkspaceSwitch';
import { SWITCH_FONT, SWITCH_LOOKS, SWITCH_TYPE, SWITCH_ZERO, type SwitchLight } from './switchLooks';

/** A light's roles, as the sheet's variables (the working mark's four inks included: it reads them). */
function lightVariables(light: SwitchLight): string {
  const k = SWITCH_LOOKS[light];
  return [
    `--pv-ws-sky: ${k.sky};`,
    `--pv-ws-ink: ${k.ink};`,
    `--pv-ws-ink-2: ${k.ink2};`,
    `--pv-ws-eyebrow: ${k.eyebrow};`,
    `--pv-ws-edge: ${k.edge};`,
    `--mark-spin-ink: ${k.spin.ink};`,
    `--mark-spin-gold-hi: ${k.spin.goldHi};`,
    `--mark-spin-gold-body: ${k.spin.goldBody};`,
    `--mark-spin-gold-lo: ${k.spin.goldLo};`,
  ].join(' ');
}

const T = SWITCH_TYPE;

export const WORKSPACE_SWITCH_CSS = `
.pv-ws { position: fixed; inset: 0; z-index: ${BODY_PORTAL_Z_INDEX}; box-sizing: border-box; margin: 0; padding: 24px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: var(--pv-ws-sky); color: var(--pv-ws-ink); font-family: ${SWITCH_FONT}; line-height: 1.2; text-align: center;
  text-rendering: geometricPrecision; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
  opacity: 1; transition: opacity ${SWITCH_FADE_MS}ms ease; animation: pv-ws-in .24s ease-out; }
.pv-ws[data-light="noon"] { ${lightVariables('noon')} color-scheme: light; }
.pv-ws[data-light="dusk"] { ${lightVariables('dusk')} color-scheme: dark; }
.pv-ws[data-leaving] { opacity: 0; }
@keyframes pv-ws-in { from { opacity: 0; } to { opacity: 1; } }
.pv-ws *, .pv-ws *::before, .pv-ws *::after { box-sizing: border-box; }
.pv-ws-card { display: flex; flex-direction: column; align-items: center; max-width: min(440px, calc(100vw - 48px)); outline: none; }
.pv-ws-card:focus, .pv-ws-card:focus-visible { outline: none; }
.pv-ws-wordmark { display: inline-flex; align-items: center; justify-content: center; margin: 0; padding: 0 1.25rem;
  font-family: ${SWITCH_FONT}; font-size: ${T.wordmark.size}; line-height: ${T.wordmark.lineHeight}; font-weight: ${T.wordmark.weight};
  letter-spacing: ${T.wordmark.tracking};
  color: var(--pv-ws-ink); text-transform: lowercase; }
.pv-ws-zero { display: block; flex: none; height: ${SWITCH_ZERO.height}; width: ${SWITCH_ZERO.width}; overflow: visible;
  margin: ${SWITCH_ZERO.margin}; margin-bottom: ${SWITCH_ZERO.marginBottom}; color: var(--pv-ws-ink); }
.pv-ws-eyebrow { margin: ${T.eyebrow.marginTop} 0 0; font-size: ${T.eyebrow.size}; line-height: ${T.eyebrow.lineHeight};
  font-weight: ${T.eyebrow.weight}; letter-spacing: ${T.eyebrow.tracking}; color: var(--pv-ws-eyebrow); text-transform: lowercase; }
.pv-ws-org { margin: ${T.org.marginTop} 0 0; max-width: min(440px, calc(100vw - 48px)); font-size: ${T.org.size};
  line-height: ${T.org.lineHeight}; font-weight: ${T.org.weight}; letter-spacing: ${T.org.tracking}; color: var(--pv-ws-ink);
  overflow-wrap: anywhere; }
.pv-ws-thread { margin: ${T.thread.marginTop} 0 0; font-size: ${T.thread.size}; line-height: ${T.thread.lineHeight};
  color: var(--pv-ws-ink-2); overflow-wrap: anywhere; }
.pv-ws-usage { margin: ${T.usage.marginTop} 0 0; padding: ${T.usage.padding}; border: 1px solid var(--pv-ws-edge); border-radius: 999px;
  font-size: ${T.usage.size}; line-height: ${T.usage.lineHeight}; color: var(--pv-ws-ink-2); overflow-wrap: anywhere; }
.pv-ws-failed { margin: ${T.failed.marginTop} 0 0; font-size: ${T.failed.size}; line-height: ${T.failed.lineHeight};
  color: var(--pv-ws-ink-2); }
.pv-ws-spin { margin-top: ${T.spin.marginTop}; }
@media (prefers-reduced-motion: reduce) { .pv-ws { animation: none; transition: none; } }
`;
